<template>
  <!-- MYAC-1269 — share the project conversation on screen with other members of
       its project (from the mockup, front PR 868). Private until shared; two
       rights, read or write; the whole project in read-only or edit mode; and a
       sentence that says, before anything happens, who will see what. -->
  <Dialog :visible="visible" modal :header="t('conversations.share.title')" :style="{ width: '34rem' }"
    :breakpoints="{ '640px': '96vw' }" data-testid="share-dialog" @update:visible="(v: boolean) => !v && emit('close')">
    <p class="m-0 mb-3 text-sm text-surface-600">{{ t('conversations.share.intro', { project: projectName }) }}</p>

    <!-- Until the project's roster is here, nothing can be ticked or saved: an
         empty list is not « nobody », and « Enregistrer » on it would read as
         « remove everyone » (QA of MYAC-1269, D4). -->
    <Message v-if="membersFailed" severity="warn" :closable="false" data-testid="share-members-failed">
      {{ t('conversations.share.members_failed') }}
    </Message>
    <p v-else-if="!membersReady" class="m-0 text-sm text-surface-600" data-testid="share-loading">
      <i class="pi pi-spin pi-spinner mr-1" aria-hidden="true" />{{ t('conversations.share.loading') }}
    </p>
    <p v-else-if="!candidates.length" class="m-0 text-sm text-surface-600" data-testid="share-nobody">
      {{ t('conversations.share.nobody', { project: projectName }) }}
    </p>
    <ul v-else class="people" role="list" :aria-label="t('conversations.share.people_label')">
      <li v-for="m in candidates" :key="m.uid" class="person" :data-testid="`share-person-${m.uid}`">
        <Checkbox v-model="picked" :input-id="`share-${m.uid}`" :value="m.uid" @update:model-value="dirty = true" />
        <label :for="`share-${m.uid}`" class="person-name">
          <span class="min-w-0 truncate">{{ m.name }}</span>
          <!-- Named on it but no longer a member: can be unticked, nothing else. -->
          <Tag v-if="m.former" :value="t('conversations.share.former_member')" severity="secondary"
            data-testid="share-former-member" />
        </label>
        <SelectButton v-if="picked.includes(m.uid)" :model-value="rights[m.uid] ?? 'read'" :options="RIGHT_OPTIONS"
          option-label="label" option-value="value" :allow-empty="false" size="small" :disabled="m.former"
          :aria-label="t('conversations.share.right_label', { name: m.name })"
          @update:model-value="(v: ShareRight) => { rights[m.uid] = v; dirty = true }" />
      </li>
    </ul>
    <p v-if="tooMany" class="m-0 mt-2 text-sm text-red-700" role="alert" data-testid="share-too-many">
      {{ t('conversations.share.too_many', { max: MAX_SHARE_PARTICIPANTS }) }}
    </p>

    <div class="project-mode mt-3">
      <span class="text-sm font-medium text-surface-800">{{ t('conversations.share.project_label') }}</span>
      <SelectButton v-model="projectMode" :options="PROJECT_OPTIONS" option-label="label" option-value="value"
        :allow-empty="false" size="small" class="mt-1" data-testid="share-project-mode" :disabled="!membersReady"
        :aria-label="t('conversations.share.project_label')" @update:model-value="dirty = true" />
    </div>

    <!-- The sentence that says, before anything happens, who will see what —
         only once the roster is in: before, it would call a shared conversation
         « visible par vous seulement » (seen on screen, QA 2026-10-22). -->
    <p v-if="membersReady" class="consequence" role="status" data-testid="share-consequence">
      <i :class="isShared ? 'pi pi-users' : 'pi pi-lock'" aria-hidden="true" />
      <span>{{ consequence }}</span>
    </p>

    <template #footer>
      <div class="flex items-center w-full gap-2">
        <Button v-if="wasShared" :label="t('conversations.share.stop')" text severity="secondary" size="small"
          :disabled="busy" data-testid="share-stop" @click="stop" />
        <span class="flex-1" />
        <Button :label="t('common.cancel')" text severity="secondary" :disabled="busy" @click="emit('close')" />
        <Button :label="wasShared ? t('conversations.share.save') : t('conversations.share.confirm')"
          :disabled="!changed || tooMany || busy" :loading="busy" data-testid="share-confirm" @click="confirm" />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from 'primevue/dialog'
import Checkbox from 'primevue/checkbox'
import SelectButton from 'primevue/selectbutton'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Message from 'primevue/message'
import { useMembersStore } from '@/stores/members'
import { useUsersStore } from '@/stores/users'
import { useProjectsStore } from '@/stores/projects'
import { useChatbotStore } from '@/stores/chatbot'
import {
  MAX_SHARE_PARTICIPANTS, sharingChanged, sharingDelta, useConversationsStore, type ProjectShareMode,
} from '@/stores/conversations'
import type { ShareRight } from '@/composables/useConversationCommands'
import { memberName } from '@/utils/userDisplay'

const props = defineProps<{ visible: boolean }>()
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const members = useMembersStore()
const users = useUsersStore()
const projects = useProjectsStore()
const chatbot = useChatbotStore()
const conversations = useConversationsStore()

const RIGHT_OPTIONS = computed(() => [
  { value: 'read' as ShareRight, label: t('conversations.share.right.read') },
  { value: 'write' as ShareRight, label: t('conversations.share.right.write') },
])
const PROJECT_OPTIONS = computed(() => [
  { value: 'off' as ProjectShareMode, label: t('conversations.share.project.off') },
  { value: 'read' as ProjectShareMode, label: t('conversations.share.project.read') },
  { value: 'write' as ProjectShareMode, label: t('conversations.share.project.write') },
])

const projectId = computed(() => chatbot.projectId)
const chatId = computed(() => chatbot.sessionId)
const projectName = computed(() => projects.projects.find(p => p.id === projectId.value)?.name ?? '')
const row = computed(() => chatbot.chatRow)
const busy = computed(() => !!chatId.value && conversations.sharing[chatId.value] === true)

/** What the row says today, in the dialog's own terms. */
const current = computed(() => ({
  participants: Object.fromEntries((row.value?.participant_uids ?? []).map(uid =>
    [uid, (row.value?.writer_uids ?? []).includes(uid) ? 'write' : 'read'] as [string, ShareRight])),
  project: (row.value?.visibility === 'project'
    ? (row.value.project_right === 'write' ? 'write' : 'read') : 'off') as ProjectShareMode,
}))
const wasShared = computed(() => Object.keys(current.value.participants).length > 0 || current.value.project !== 'off')

/** The roster of THIS project is in (an empty one is « nobody », not « not yet »). */
const membersReady = computed(() => !!projectId.value && members.projectMembersLoadedFor === projectId.value)
/** Its read failed: said, and nothing can be saved — never « loading » forever, never « nobody ». */
const membersFailed = computed(() => !membersReady.value && members.projectMembersFailed === true)

/**
 * The project's other members, by name — only they can be given access
 * (Sébastien, 2026-10-02) — and, after them, anyone still named on the
 * conversation who left the project: listed so the owner can untick them,
 * never a raw uid (the tenant roster names them when it can).
 */
const candidates = computed(() => {
  if (!membersReady.value) return []
  const me = users.user?.uid
  const people = members.projectMembers
    .filter(m => m.uid && m.uid !== me && m.active !== false)
    .map(m => ({ uid: m.uid!, name: memberName(m) || t('shared.unknown_user'), former: false }))
    .sort((a, b) => a.name.localeCompare(b.name))
  const listed = new Set(people.map(p => p.uid))
  const former = Object.keys(current.value.participants)
    .filter(uid => uid !== me && !listed.has(uid))
    .map(uid => ({
      uid, name: memberName(members.tenantMembers.find(m => m.uid === uid)) || t('shared.unknown_user'), former: true,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
  return [...people, ...former]
})

const picked = ref<string[]>([])
const rights = reactive<Record<string, ShareRight>>({})
const projectMode = ref<ProjectShareMode>('off')
/** The person touched the form: a row change underneath no longer resets it. */
const dirty = ref(false)

function reset(): void {
  picked.value = Object.keys(current.value.participants)
  for (const key of Object.keys(rights)) delete rights[key]
  for (const [uid, right] of Object.entries(current.value.participants)) rights[uid] = right
  projectMode.value = current.value.project
  void nextTick(() => { dirty.value = false })
}
watch(() => props.visible, (open) => {
  if (!open) return
  if (projectId.value) void members.listProjectMembers(projectId.value)
  reset()
}, { immediate: true })
// The sharing changed under the open dialog (another tab, a participant who
// left): an untouched form follows the row; one being edited keeps its edits.
watch(current, () => { if (props.visible && !dirty.value) reset() })
watch(picked, (now) => { for (const uid of now) if (!rights[uid]) rights[uid] = 'read' })

const chosen = computed(() => candidates.value.filter(c => picked.value.includes(c.uid))
  .map(c => ({ ...c, right: rights[c.uid] ?? 'read' as ShareRight })))
const wanted = computed(() => ({
  participants: Object.fromEntries(chosen.value.map(p => [p.uid, p.right])) as Record<string, ShareRight>,
  project: projectMode.value,
}))
const isShared = computed(() => chosen.value.length > 0 || projectMode.value !== 'off')
const tooMany = computed(() => chosen.value.length > MAX_SHARE_PARTICIPANTS)

/** The people the dialog could show: only they can be removed by being unticked. */
const known = computed(() => candidates.value.map(c => c.uid))
const changed = computed(() => membersReady.value && sharingChanged(sharingDelta(current.value, wanted.value, known.value)))

function names(list: string[]): string {
  if (list.length <= 1) return list[0] ?? ''
  return t('conversations.share.and', { first: list.slice(0, -1).join(', '), last: list[list.length - 1] })
}

const consequence = computed(() => {
  const list = chosen.value
  const parts: string[] = []
  if (projectMode.value === 'write') parts.push(t('conversations.share.consequence_project_write', { project: projectName.value }))
  else if (projectMode.value === 'read') parts.push(t('conversations.share.consequence_project_read', { project: projectName.value }))
  const writers = list.filter(p => p.right === 'write').map(p => p.name)
  const readers = list.filter(p => p.right === 'read' && projectMode.value !== 'write').map(p => p.name)
  // under a project-wide edit share, a named reader writes too (the stronger right wins)
  if (writers.length && projectMode.value !== 'write') {
    parts.push(t('conversations.share.consequence_write', { names: names(writers) }, writers.length))
  }
  if (readers.length && projectMode.value === 'off') {
    parts.push(t('conversations.share.consequence_read', { names: names(readers) }, readers.length))
  }
  if (!parts.length) return t('conversations.share.consequence_private')
  return `${parts.join(' ')} ${t('conversations.share.consequence_tail')}`
})

async function confirm(): Promise<void> {
  if (!projectId.value || !chatId.value || !changed.value || tooMany.value) return
  const ok = await conversations.applySharing(projectId.value, chatId.value, current.value, wanted.value, known.value)
  if (ok) emit('close')
}

async function stop(): Promise<void> {
  if (!projectId.value || !chatId.value) return
  const ok = await conversations.stopSharing(projectId.value, chatId.value)
  if (ok) emit('close')
}
</script>

<style scoped>
.people { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; max-height: 18rem; overflow-y: auto; }
.person { display: flex; align-items: center; gap: 10px; min-height: 44px; padding: 4px 6px; border-radius: 6px; }
.person:hover { background: var(--p-surface-50); }
.person-name { flex: 1; min-width: 0; display: flex; align-items: center; gap: 10px; font-size: 14px; cursor: pointer; }
.project-mode { display: flex; flex-direction: column; }
.consequence {
  display: flex; gap: 10px; align-items: flex-start; margin: 14px 0 0; padding: 10px 12px; border-radius: 6px;
  background: var(--p-primary-50); border: 1px solid var(--p-primary-100); font-size: 13.5px; line-height: 1.55;
  color: var(--p-surface-800);
}
.consequence .pi { color: var(--p-primary-color); margin-top: 3px; }
</style>
